/**
 * Session state for the terminal: tokens, `/auth/me`, the active branch, and
 * the API client every screen uses.
 *
 * The client is created ONCE and kept in a ref. Recreating it on every render
 * would recreate the single-flight refresh lock, and six screens hitting an
 * expired token at the same moment would each send their own refresh — which
 * the API treats as token theft and answers by burning the whole session
 * family.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { ApiClient, AuthTokens } from '@sonauto/api-client';
import { createPosApi, createPosClient } from '../lib/api';
import type { PosApi } from '../lib/api';
import { loadTokens, saveTokens, saveCashSessionId } from '../lib/storage';
import type { MeResponse } from '../lib/types';

export interface AuthState {
  readonly api: PosApi;
  readonly client: ApiClient;
  readonly me: MeResponse | null;
  readonly loadingMe: boolean;
  readonly meError: string | null;
  readonly signedIn: boolean;
  readonly branchId: string | null;
  readonly permissions: ReadonlySet<string>;
  readonly permissionsByBranch: Readonly<Record<string, ReadonlySet<string>>>;
  readonly setTokens: (tokens: AuthTokens | null) => void;
  readonly signOut: () => Promise<void>;
  readonly reloadMe: () => void;
  readonly switchBranch: (branchId: string) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (ctx === null) throw new Error('useAuth phải được dùng bên trong <AuthProvider>.');
  return ctx;
}

/** Convenience: the api, without pulling the whole auth object. */
export function useApi(): PosApi {
  return useAuth().api;
}

export function AuthProvider({ children }: { readonly children: ReactNode }): React.JSX.Element {
  const [tokens, setTokensState] = useState<AuthTokens | null>(() => loadTokens());
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loadingMe, setLoadingMe] = useState(false);
  const [meError, setMeError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // The live token value the client reads. Kept in a ref so the client's
  // getTokens closure always sees the current value without being recreated.
  const tokensRef = useRef<AuthTokens | null>(tokens);
  tokensRef.current = tokens;

  const applyTokens = useCallback((next: AuthTokens | null) => {
    tokensRef.current = next;
    setTokensState(next);
    saveTokens(next);
    if (next === null) {
      setMe(null);
      // A till belongs to a cashier's shift, not to the terminal. Forgetting it
      // on sign-out stops the next user inheriting someone else's drawer.
      saveCashSessionId(null);
    }
  }, []);

  const clientRef = useRef<ApiClient | null>(null);
  if (clientRef.current === null) {
    clientRef.current = createPosClient({
      getTokens: () => tokensRef.current,
      setTokens: applyTokens,
      onUnauthenticated: () => applyTokens(null),
    });
  }
  const client = clientRef.current;

  const api = useMemo(() => createPosApi(client), [client]);

  useEffect(() => {
    if (tokens === null) {
      setMe(null);
      setMeError(null);
      return undefined;
    }
    let alive = true;
    const controller = new AbortController();
    setLoadingMe(true);
    api.auth
      .me({ signal: controller.signal })
      .then((value) => {
        if (!alive) return;
        setMe(value);
        setMeError(null);
      })
      .catch((cause: unknown) => {
        if (!alive || controller.signal.aborted) return;
        setMeError(cause instanceof Error ? cause.message : 'Không tải được thông tin phiên đăng nhập.');
      })
      .finally(() => {
        if (alive) setLoadingMe(false);
      });
    return () => {
      alive = false;
      controller.abort();
    };
  }, [api, tokens, nonce]);

  const signOut = useCallback(async () => {
    try {
      if (tokensRef.current !== null) await api.auth.logout();
    } catch {
      // A failed logout must never trap a user on a shared terminal. The local
      // tokens are dropped either way; the server session expires on its own.
    }
    applyTokens(null);
  }, [api, applyTokens]);

  const switchBranch = useCallback(
    async (branchId: string) => {
      await api.auth.switchBranch(branchId);
      setNonce((n) => n + 1);
    },
    [api],
  );

  const permissions = useMemo<ReadonlySet<string>>(() => new Set(me?.permissions ?? []), [me]);

  const permissionsByBranch = useMemo<Readonly<Record<string, ReadonlySet<string>>>>(() => {
    const out: Record<string, ReadonlySet<string>> = {};
    for (const [branchId, codes] of Object.entries(me?.permissionsByBranch ?? {})) {
      out[branchId] = new Set(codes);
    }
    return out;
  }, [me]);

  const value = useMemo<AuthState>(
    () => ({
      api,
      client,
      me,
      loadingMe,
      meError,
      signedIn: tokens !== null,
      branchId: me?.session.activeBranchId ?? null,
      permissions,
      permissionsByBranch,
      setTokens: applyTokens,
      signOut,
      reloadMe: () => setNonce((n) => n + 1),
      switchBranch,
    }),
    [
      api,
      client,
      me,
      loadingMe,
      meError,
      tokens,
      permissions,
      permissionsByBranch,
      applyTokens,
      signOut,
      switchBranch,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
